import {
  ChangeDetectionStrategy,
  Component,
  input,
  signal,
  effect,
  output,
  computed,
} from '@angular/core';
import { IconComponent } from '../../atoms/icon/icon.component';
import { Dayjs } from 'dayjs';
import dayjs from '../../../configs/dayjs.config';
import { PaginatorIntervalTypeEnum } from './paginator.enum';

@Component({
  selector: 'app-paginator',
  imports: [IconComponent],
  templateUrl: './paginator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginatorComponent {
  readonly intervalType = input.required<PaginatorIntervalTypeEnum>();

  readonly paginationChange = output<{ index: number; year: number }>();

  private readonly today = signal<Dayjs>(dayjs());
  private readonly index = signal<number>(this.today().month());
  private readonly year = signal<number>(this.today().year());

  readonly label = computed((): string => {
    const type = this.intervalType();
    const currentIndex = this.index();
    const currentYear = this.year();
    switch (type) {
      case PaginatorIntervalTypeEnum.WEEK:
        const start = dayjs().year(this.year()).isoWeek(this.index()).startOf('week');
        const end = dayjs().year(this.year()).isoWeek(this.index()).endOf('week');

        return `${start.format('D. MMM')} bis ${end.format('D. MMM YYYY')}`;

      case PaginatorIntervalTypeEnum.MONTH:
        return dayjs().year(currentYear).month(currentIndex).format('MMMM YYYY');

      default:
        return '';
    }
  });

  private readonly init = effect(() => {
    switch (this.intervalType()) {
      case PaginatorIntervalTypeEnum.WEEK:
        this.index.set(this.today().isoWeek());
        break;
      case PaginatorIntervalTypeEnum.MONTH:
        this.year.set(this.today().year());
        break;
    }
  });

  private readonly emitEvent = effect(() =>
    this.paginationChange.emit({ index: this.index(), year: this.year() })
  );

  public next(): void {
    const maxIndex = this.intervalType() === PaginatorIntervalTypeEnum.WEEK ? 52 : 12;

    if (this.index() === maxIndex) {
      this.index.set(1);
      this.year.update((value) => value + 1);
    } else {
      this.index.update((value) => value + 1);
    }
  }

  public previous(): void {
    const maxIndex = this.intervalType() === PaginatorIntervalTypeEnum.WEEK ? 52 : 12;

    if (this.index() === 1) {
      this.index.set(maxIndex);
      this.year.update((value) => value - 1);
    } else {
      this.index.update((value) => value - 1);
    }
  }
}
